import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { Message, AIContextType, AIProviderId } from '../types';
import { useWriter } from '../hooks/useWriter';
import { createWritingPrompt } from '../prompts/writingPrompts';
import { sendPromptToGemini, getGeminiConnectionStatus } from '@core/ai';
import { db } from '@core/db';
import { AIContext } from './aiContext';

const checkOllamaConnection = async (): Promise<boolean> => {
    // Mock implementation for Ollama connection check
    // In a real scenario, this would ping a local server endpoint e.g., http://localhost:11434
    try {
        const response = await fetch('http://localhost:11434/api/version');
        return response.ok;
    } catch {
        return false;
    }
};

const sendPromptToOllama = async (prompt: string): Promise<string> => {
    // Mock implementation for sending prompt to Ollama
    console.log("Sending to Ollama (mock):", prompt);
    await new Promise(res => setTimeout(res, 1500));
    return "Respuesta de Ollama (mock): Gracias por tu pregunta. Esta es una funcionalidad de demostración.";
};

export const AIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { selectedGenre, activeProject, activeSection, activeVersionId, sections } = useWriter();
  
  const [currentProvider, setCurrentProvider] = useState<AIProviderId>(() => {
    return (localStorage.getItem('aiProvider') as AIProviderId) || AIProviderId.GEMINI;
  });
  
  const getInitialMessage = useCallback(() => {
    const genreName = selectedGenre?.name || 'textos increíbles';
    return {
      id: '1',
      role: 'assistant' as const,
      content: `¡Hola! Soy tu coach de escritura para ${genreName}. ¿Cómo puedo ayudarte a empezar?`,
      timestamp: new Date(),
      provider: currentProvider,
    };
  }, [selectedGenre, currentProvider]);

  const [messages, setMessages] = useState<Message[]>(() => [getInitialMessage()]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionHint, setConnectionHint] = useState<string | undefined>(undefined);
  
  // Resets the chat and updates the welcome message when the genre changes.
  useEffect(() => {
    setMessages([getInitialMessage()]);
  }, [getInitialMessage]);

  const checkConnection = useCallback(async () => {
    let connectionStatus = false;
    let hint: string | undefined;
    try {
      if (currentProvider === AIProviderId.GEMINI) {
        const status = await getGeminiConnectionStatus();
        connectionStatus = status.ok;
        if (status.reason === 'local_netlify_dev_required') {
          hint = 'IA remota no disponible en Vite localhost. Usa netlify dev para exponer las Netlify Functions.'
        }
      } else if (currentProvider === AIProviderId.OLLAMA) {
        connectionStatus = await checkOllamaConnection();
      }
    } catch (error) {
      console.error('Error checking connection:', error);
      connectionStatus = false;
      hint = undefined;
    }
    setIsConnected(connectionStatus);
    setConnectionHint(hint);
  }, [currentProvider]);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  const updateProvider = (provider: AIProviderId) => {
    localStorage.setItem('aiProvider', provider);
    setCurrentProvider(provider);
  };
  
  const addMessage = useCallback((content: string, role: 'user' | 'assistant', provider?: AIProviderId) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      role,
      content,
      timestamp: new Date(),
      provider: provider || currentProvider,
    };
    setMessages(prev => [...prev, newMessage]);
  }, [currentProvider]);

  const sendMessage = async (content: string) => {
    // Defensive logging for debugging unexpected calls
    console.log('[AIProvider] sendMessage called', {
      content,
      activeProject: activeProject?.id,
      selectedGenre: selectedGenre?.id,
      currentProvider,
      stack: new Error().stack,
    });
    if (!activeProject || !selectedGenre || !activeVersionId) {
        addMessage("Por favor, selecciona un género y una versión activa primero.", 'assistant');
        return;
    }
    
    addMessage(content, 'user');
    setIsLoading(true);

    try {
      // Usa las secciones de la versión activa expuestas por el WriterProvider
      const contentPromises = (sections || []).map(async (section) => {
          const record = await db.sectionContents
            .where({ sectionId: section.id, versionId: activeVersionId })
            .first();
          return { sectionName: section.name, content: record?.content || '' };
      });

      const allContent = await Promise.all(contentPromises);
      const structuredContent = allContent.reduce((acc, curr) => {
          acc[curr.sectionName] = curr.content;
          return acc;
      }, {} as { [key: string]: string });

      const prompt = createWritingPrompt(
        selectedGenre.id, 
        content, 
        structuredContent,
        activeSection?.name || 'ninguna sección activa'
      );
      
      let response = '';

      if (currentProvider === AIProviderId.GEMINI) {
        response = await sendPromptToGemini(prompt);
      } else if (currentProvider === AIProviderId.OLLAMA) {
        response = await sendPromptToOllama(prompt); // MOCK CALL
      } else {
        response = "El proveedor de IA seleccionado no está configurado.";
      }
      
      addMessage(response, 'assistant');
    } catch (error: unknown) {
      console.error('Error with AI:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      addMessage(
        `🔴 Error: ${errorMessage}\n\n🤖 Verifica tu configuración de IA en el panel de configuración.\n\n💡 En despliegue, configura GEMINI_API_KEY en el entorno servidor (Netlify Function).`,
        'assistant'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: AIContextType = {
    messages,
    addMessage,
    isLoading,
    sendMessage,
    currentProvider,
    isConnected,
    connectionHint,
    checkConnection,
    updateProvider
  };

  return <AIContext.Provider value={contextValue}>{children}</AIContext.Provider>;
};
